import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { cn } from "../lib/utils";
import {
  User,
  Search,
  Layers,
  X,
  AlertTriangle,
  DollarSign,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// Specialization colors
const SPECIALIZATION_COLORS = {
  exterior: "#3b82f6",
  interior: "#8b5cf6", 
  aerial: "#06b6d4",
  animation: "#f59e0b",
  floor_plan: "#10b981",
  modeller: "#ec4899",
  other: "#6b7280",
};

const SPECIALIZATION_LABELS = {
  exterior: "Ext",
  interior: "Int",
  aerial: "Air",
  animation: "Anim",
  floor_plan: "FP",
  modeller: "Mod",
  other: "Oth",
};

// Format type label
const formatTypeLabel = (key) => {
  if (!key) return "";
  return key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

const ArtistSelector = ({ selectedArtistId, onSelect, taskType }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [artists, setArtists] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [expandedArtistId, setExpandedArtistId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchArtists();
    }
  }, [open]);

  useEffect(() => {
    if (selectedArtistId && artists.length > 0) {
      const artist = artists.find(a => a.id === selectedArtistId);
      setSelectedArtist(artist);
    } else if (!selectedArtistId) {
      setSelectedArtist(null);
    }
  }, [selectedArtistId, artists]);

  const fetchArtists = async () => {
    setLoading(true);
    try {
      const res = await api.get("/artists/stats");
      setArtists(res.data || []);
    } catch (error) {
      console.error("Error fetching artists:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (artist) => {
    setSelectedArtist(artist);
    onSelect(artist?.id || "", artist || null);
    setOpen(false);
  };

  const clearSelection = () => {
    setSelectedArtist(null);
    onSelect("", null);
  };

  const toggleExpanded = (e, artistId) => {
    e.stopPropagation();
    setExpandedArtistId(expandedArtistId === artistId ? null : artistId);
  };

  // Filter artists by search query
  const filteredArtists = artists.filter(a => 
    a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort: less loaded first, then by on-time rate
  const sortedArtists = [...filteredArtists].sort((a, b) => {
    if (a.stats.active_tasks !== b.stats.active_tasks) {
      return a.stats.active_tasks - b.stats.active_tasks;
    }
    return b.stats.on_time_rate - a.stats.on_time_rate;
  });

  // Get workload status - compact
  const getStatusBadge = (stats) => {
    if (stats.overdue_tasks > 0) return { label: "!", color: "text-red-400 bg-red-900/30" };
    if (stats.active_tasks === 0) return { label: "Free", color: "text-green-400 bg-green-900/30" };
    if (stats.active_tasks <= 2) return { label: stats.active_tasks, color: "text-green-400 bg-green-900/30" };
    if (stats.active_tasks <= 4) return { label: stats.active_tasks, color: "text-yellow-400 bg-yellow-900/30" };
    return { label: stats.active_tasks, color: "text-red-400 bg-red-900/30" };
  };

  return (
    <div>
      {/* Trigger Button - Compact */}
      <div 
        onClick={() => setOpen(true)}
        className="flex items-center justify-between p-2 bg-zinc-950 border border-zinc-800 rounded-lg cursor-pointer hover:border-zinc-700 transition-colors"
      >
        {selectedArtist ? (
          <div className="flex items-center gap-2 flex-1">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-medium text-xs">
              {selectedArtist.name?.charAt(0)}
            </div>
            <span className="text-sm text-zinc-50 truncate">{selectedArtist.name}</span>
            {selectedArtist.desired_rates?.[taskType] && (
              <span className="text-xs text-yellow-500">
                (wants ${selectedArtist.desired_rates[taskType]})
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); clearSelection(); }}
              className="text-zinc-500 hover:text-zinc-50 h-5 w-5 p-0 ml-auto"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <User className="h-4 w-4" />
            <span>Select artist or Pool</span>
          </div>
        )}
      </div>

      {/* Selection Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[75vh] overflow-hidden flex flex-col p-4">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-zinc-50 text-base flex items-center gap-2">
              <User className="h-4 w-4 text-orange-500" />
              Select Artist
              {taskType && (
                <span className="text-xs text-zinc-500 font-normal">
                  for {formatTypeLabel(taskType)}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-zinc-500" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-zinc-50 pl-7 h-8 text-sm"
            />
          </div>

          {/* Pool Option - Compact */}
          <div
            onClick={() => handleSelect(null)}
            className="flex items-center gap-2 p-2 border border-dashed border-zinc-700 rounded cursor-pointer hover:border-purple-500 hover:bg-purple-900/10 transition-colors"
          >
            <Layers className="h-4 w-4 text-purple-400" />
            <span className="text-sm text-purple-400">Add to Pool</span>
            <span className="text-xs text-zinc-500">— any artist can take</span>
          </div>

          {/* Artists List */}
          <div className="flex-1 overflow-y-auto space-y-1 mt-2">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-orange-500" />
              </div>
            ) : sortedArtists.length === 0 ? (
              <p className="text-center text-zinc-500 py-4 text-sm">No artists found</p>
            ) : (
              sortedArtists.map((artist) => {
                const stats = artist.stats || {};
                const status = getStatusBadge(stats);
                const isRecommended = stats.active_tasks <= 2 && stats.overdue_tasks === 0 && stats.on_time_rate >= 80;
                const hasRates = artist.desired_rates && Object.keys(artist.desired_rates).length > 0;
                const isExpanded = expandedArtistId === artist.id;
                const artistRateForType = artist.desired_rates?.[taskType];
                
                return (
                  <div key={artist.id} className="border border-transparent hover:border-zinc-700 rounded transition-all">
                    {/* Main Row - Compact */}
                    <div
                      onClick={() => handleSelect(artist)}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded cursor-pointer transition-all",
                        selectedArtistId === artist.id
                          ? "bg-orange-900/20 border border-orange-500"
                          : "hover:bg-zinc-800/50",
                        isRecommended && selectedArtistId !== artist.id && "bg-green-900/10"
                      )}
                    >
                      {/* Avatar */}
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-medium text-xs shrink-0">
                        {artist.name?.charAt(0)}
                      </div>

                      {/* Name */}
                      <span className="text-sm text-zinc-50 font-medium w-[130px] truncate">
                        {artist.name}
                      </span>

                      {/* Status Badge */}
                      <span className={cn("text-xs px-1.5 py-0.5 rounded font-mono", status.color)}>
                        {status.label}
                      </span>

                      {/* On-time rate */}
                      <span className={cn(
                        "text-xs font-mono w-10",
                        stats.on_time_rate >= 80 ? "text-green-400" : 
                        stats.on_time_rate >= 60 ? "text-yellow-400" : "text-red-400"
                      )}>
                        {stats.on_time_rate}%
                      </span>

                      {/* Artist's rate for THIS task type - highlighted */}
                      {artistRateForType ? (
                        <span className="text-xs text-yellow-400 bg-yellow-900/30 px-1.5 py-0.5 rounded font-mono">
                          ${artistRateForType}
                        </span>
                      ) : hasRates ? (
                        <span className="text-xs text-zinc-500 px-1.5 py-0.5">
                          —
                        </span>
                      ) : null}

                      {/* Specializations - compact */}
                      <div className="flex gap-0.5 flex-1 min-w-0">
                        {(artist.specializations || []).slice(0, 3).map(spec => (
                          <span
                            key={spec}
                            className="text-[10px] px-1 rounded"
                            style={{
                              backgroundColor: `${SPECIALIZATION_COLORS[spec]}30`,
                              color: SPECIALIZATION_COLORS[spec],
                            }}
                          >
                            {SPECIALIZATION_LABELS[spec] || spec.slice(0,3)}
                          </span>
                        ))}
                        {(artist.specializations?.length || 0) > 3 && (
                          <span className="text-[10px] text-zinc-500">+{artist.specializations.length - 3}</span>
                        )}
                      </div>

                      {/* Overdue warning */}
                      {stats.overdue_tasks > 0 && (
                        <AlertTriangle className="h-3 w-3 text-red-400 shrink-0" />
                      )}

                      {/* Recommended badge */}
                      {isRecommended && (
                        <span className="text-[10px] text-green-400 bg-green-900/30 px-1 rounded shrink-0">✓</span>
                      )}

                      {/* Expand rates button */}
                      {hasRates && (
                        <button
                          onClick={(e) => toggleExpanded(e, artist.id)}
                          className="text-zinc-500 hover:text-zinc-50 p-0.5"
                        >
                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </button>
                      )}
                    </div>

                    {/* Expanded Rates Row */}
                    {isExpanded && hasRates && (
                      <div className="px-2 pb-2 pt-1 ml-9 flex flex-wrap gap-x-3 gap-y-1 text-xs border-t border-zinc-800/50">
                        {Object.entries(artist.desired_rates).map(([type, rate]) => (
                          <span 
                            key={type} 
                            className={cn(
                              "text-zinc-400",
                              type === taskType && "text-yellow-400 font-medium"
                            )}
                          >
                            {formatTypeLabel(type)}: <span className="font-mono">${rate}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 pt-2 border-t border-zinc-800 text-[10px] text-zinc-500">
            <span className="flex items-center gap-1">
              <span className="text-green-400">✓</span> Recommended
            </span>
            <span className="flex items-center gap-1">
              <span className="text-yellow-400 bg-yellow-900/30 px-1 rounded font-mono">$</span> Rate for selected type
            </span>
            <span className="flex items-center gap-1">
              <ChevronDown className="h-3 w-3" /> Show all rates
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtistSelector;
